import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useCreateDashboard, useDashboards } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { ChartIcon, DashboardIcon, DatabaseIcon, PlusIcon } from "@/components/icons";
import { useRelativeTime } from "@/lib/time";

/** Cross-connection tile grids (PRD FR-7.3). */
export function DashboardsPage() {
  const { data: dashboards, isLoading } = useDashboards();
  const create = useCreateDashboard();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col">
      <header className="border-border flex flex-wrap items-center gap-3 border-b px-6 py-4">
        <div className="min-w-0 flex-1">
          <h1 className="type-h2 text-ink">Dashboards</h1>
          <p className="type-support text-ink-secondary mt-1">
            Tiles gathered from any answer, across any source. Refresh All runs each distinct
            query once and feeds every tile that asked for it.
          </p>
        </div>
        <Button variant="primary" icon={<PlusIcon className="size-4" />} onClick={() => setOpen(true)}>
          New dashboard
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : dashboards?.length ? (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dashboards.map((dashboard) => (
              <li key={dashboard.id}>
                <DashboardCard dashboard={dashboard} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<DashboardIcon className="size-6" />}
            title="No dashboards yet"
            detail="Create one, then use “Add to…” on any chart in a conversation or on a whole report."
            action={
              <Button variant="primary" onClick={() => setOpen(true)}>
                New dashboard
              </Button>
            }
          />
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New dashboard"
        description="A grid you can fill from any conversation or report, across sources."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!name.trim()}
              onClick={() =>
                create.mutate(
                  { name },
                  {
                    onSuccess: () => {
                      setName("");
                      setOpen(false);
                    },
                  },
                )
              }
            >
              Create
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">Name</span>
          <input
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            placeholder="Weekly ops"
            className="bg-surface border-border rounded-element control-lg type-body text-ink focus:border-border-emphasized motion-fast w-full border px-3 outline-none"
          />
        </label>
      </Modal>
    </div>
  );
}

function DashboardCard({
  dashboard,
}: {
  dashboard: ReturnType<typeof useDashboards>["data"] extends (infer T)[] | undefined ? T : never;
}) {
  const stamp = useRelativeTime(dashboard.last_refreshed_at);
  return (
    <Link to="/dashboards/$dashboardId" params={{ dashboardId: dashboard.id }} className="block">
      <Card className="hover:border-border-emphasized motion-fast h-full">
        <div className="flex flex-col gap-2 px-4 py-4">
          <h2 className="type-h4 text-ink line-clamp-2">{dashboard.name}</h2>
          {dashboard.description ? (
            <p className="type-support text-ink-secondary line-clamp-2">{dashboard.description}</p>
          ) : null}
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Chip tone="neutral" icon={<ChartIcon className="size-3" />}>
              {dashboard.tile_count} tile{dashboard.tile_count === 1 ? "" : "s"}
            </Chip>
            {dashboard.connections.map((connection) => (
              <Chip key={connection} tone="neutral" icon={<DatabaseIcon className="size-3" />}>
                {connection}
              </Chip>
            ))}
          </div>
          <p className="type-caption text-ink-disabled">
            {stamp ? `refreshed ${stamp}` : "never refreshed"}
          </p>
        </div>
      </Card>
    </Link>
  );
}
